import { Inject, Injectable } from '@angular/core';
import { NETWORK_SERVICE, NetworkServiceV2, TEMPLATE_SEARCH_SERVICE, TemplateSearchService } from '@netfoundry-ui/shared/apiv2';
import { ElasticsearchService } from '@netfoundry-ui/shared/elasticsearch';
import { formatBytes } from '@netfoundry-ui/ui/rx-tx-timeline-chart';
import { EMPTY, from, Observable, of, Subscription } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

/** Hours a reading covers, oldest first. The last one is the hour in progress. */
export const THROUGHPUT_HOURS = 24;

/** The ncutilization field that carries a Ziti service id, which is what a TCP share names. */
export const THROUGHPUT_SERVICE_FIELD = 'service_id.keyword';

/** The ncutilization field that carries the identity hosting a service, which is what an agent is. */
export const THROUGHPUT_HOST_FIELD = 'host_id.keyword';

/** The ncutilization field that carries the edge router a circuit enters by, which for an IPsec tunnel is its bridge. */
export const THROUGHPUT_EDGE_ROUTER_FIELD = 'edge_router_id.keyword';

const HOUR_MS = 3_600_000;

/** How many ids are read at once, so a page of rows does not queue every request on the browser. */
const MAX_CONCURRENT_READS = 6;

/**
 * The least time the running hour is averaged over. In the first minutes of an hour little has been
 * indexed yet, and a shorter divisor would swing its rate far from the hours before it.
 */
const MIN_RUNNING_MS = 5 * 60_000;

/** The traffic of one service or host over the last {@link THROUGHPUT_HOURS} hours. */
export interface Throughput {
    /** Average bytes per second in each hour, oldest first. */
    rates: number[];
    /** Bytes in both directions over the whole window. */
    totalBytes: number;
    peakRate: number;
    /** Start of the peak hour, in epoch ms. */
    peakAt: number;
}

/** One id's answer. `throughput` is null where the read failed, so the row says nothing rather than "No traffic". */
export interface ThroughputResult {
    id: string;
    throughput: Throughput | null;
}

/** What a Traffic cell draws: a sparkline of the rates, and the text beside and over it. */
export interface ThroughputCell {
    rates: number[];
    /** Bytes over the window, such as "1.2 MB", or "No traffic". */
    label: string;
    tooltip: string;
    /** True when nothing was carried in the window. */
    idle: boolean;
}

/** Where the window starts for `now`: the top of the hour, {@link THROUGHPUT_HOURS} - 1 hours back. */
export function throughputWindowStart(now: number): number {
    return Math.floor(now / HOUR_MS) * HOUR_MS - (THROUGHPUT_HOURS - 1) * HOUR_MS;
}

/** Fold `[timestampMs, bytes]` pairs into hourly rates. Pairs outside the window are dropped. */
export function toThroughput(pairs: any[][], start: number, now: number): Throughput {
    const bytes: number[] = new Array(THROUGHPUT_HOURS).fill(0);
    for (const pair of pairs) {
        const slot = Math.floor((Number(pair[0]) - start) / HOUR_MS);
        if (slot >= 0 && slot < THROUGHPUT_HOURS) {
            bytes[slot] += Number(pair[1]) || 0;
        }
    }
    const last = THROUGHPUT_HOURS - 1;
    const running = Math.max(now - (start + last * HOUR_MS), MIN_RUNNING_MS);
    const rates = bytes.map((value, slot) => (value * 1000) / (slot === last ? running : HOUR_MS));
    const peakSlot = rates.reduce((best, rate, slot) => (rate > rates[best] ? slot : best), 0);
    return {
        rates,
        totalBytes: bytes.reduce((sum, value) => sum + value, 0),
        peakRate: rates[peakSlot],
        peakAt: start + peakSlot * HOUR_MS,
    };
}

/** Name `throughput` in a cell's label and tooltip. The sparkline draws the rates itself. */
export function throughputCell(throughput: Throughput): ThroughputCell {
    if (Math.max(...throughput.rates) <= 0) {
        return { rates: throughput.rates, label: 'No traffic', tooltip: `No traffic in the last ${THROUGHPUT_HOURS} hours`, idle: true };
    }
    const total = formatBytes(throughput.totalBytes);
    const peakHour = new Date(throughput.peakAt).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    return {
        rates: throughput.rates,
        label: total,
        tooltip: `${total} in the last ${THROUGHPUT_HOURS} hours\nPeak ${formatBytes(throughput.peakRate)}/s, in the hour from ${peakHour}`,
        idle: false,
    };
}

/**
 * Reads the traffic a frontdoor's network carried for each id a list names, one filtered request per
 * id: a share's service by {@link THROUGHPUT_SERVICE_FIELD}, an agent's identity by {@link THROUGHPUT_HOST_FIELD},
 * and an IPsec tunnel's bridge router by {@link THROUGHPUT_EDGE_ROUTER_FIELD}.
 */
@Injectable({ providedIn: 'root' })
export class FrontdoorThroughputService {
    /** The network group of each network, which the utilization templates filter on and a frontdoor does not carry. */
    private networkGroups = new Map<string, Promise<string>>();

    constructor(
        private elasticsearch: ElasticsearchService,
        @Inject(TEMPLATE_SEARCH_SERVICE) private templateService: TemplateSearchService,
        @Inject(NETWORK_SERVICE) private networkService: NetworkServiceV2
    ) {}

    /** Emits one answer per id in `ids`, as each arrives. Never errors: a failure is a null answer. */
    findThroughput(networkId: string, field: string, ids: string[], now = Date.now()): Observable<ThroughputResult> {
        if (!networkId || ids.length === 0) {
            return EMPTY;
        }
        return from(this.networkGroup(networkId)).pipe(
            catchError(() => of('')),
            switchMap((networkGroupId) => {
                if (!networkGroupId) {
                    return from(ids.map((id) => ({ id, throughput: null })));
                }
                const start = throughputWindowStart(now);
                return from(ids).pipe(
                    mergeMap((id) => this.readOne(networkId, networkGroupId, field, id, start, now), MAX_CONCURRENT_READS)
                );
            })
        );
    }

    private readOne(networkId: string, networkGroupId: string, field: string, id: string, start: number, now: number): Observable<ThroughputResult> {
        // Hourly UTC buckets, so each one lines up with a slot of the window in every time zone.
        const params = {
            ...this.templateService.getUtilizationTemplateQuery(start, now, '1d', field, id, '', networkId, networkGroupId, 'ncutilization', field),
            interval: '1h',
            timeZone: 'UTC',
        };
        return this.elasticsearch.apiTemplateSearch(this.templateService.UTILIZATION_BYTES_BY_COMPONENT_TEMPLATE, params).pipe(
            map((data: any) => {
                // An id that carried nothing has no bucket, which reads as a day of zeros.
                const series = this.templateService.getStackedUtilizationSeriesData(data) || [];
                const own = series.find((entry: any) => entry.name === id);
                return { id, throughput: toThroughput(own?.data || [], start, now) };
            }),
            catchError(() => of({ id, throughput: null }))
        );
    }

    /** Read once per network. A failed read is dropped from the cache, so the next refresh asks again. */
    private networkGroup(networkId: string): Promise<string> {
        let group = this.networkGroups.get(networkId);
        if (!group) {
            group = this.networkService.getNetwork(networkId).then((network) => network?.networkGroupId ?? '');
            group.catch(() => this.networkGroups.delete(networkId));
            this.networkGroups.set(networkId, group);
        }
        return group;
    }
}

/**
 * The readings behind one list's Traffic column, keyed by the id each row names. A page holds one per
 * list and draws each row as its answer arrives.
 */
export class ThroughputTracker {
    private readings = new Map<string, Throughput>();

    /** Ids with a read in flight, so a sort or filter does not ask for them twice. */
    private pending = new Set<string>();

    /** The cell of each reading, built once per reading. */
    private cells = new WeakMap<Throughput, ThroughputCell>();

    private subscription = new Subscription();

    constructor(private service: FrontdoorThroughputService, private field: string) {}

    /** The cell for `id`, or null for a dash: no id, not read yet, or its read failed. None of these is "No traffic". */
    cell(id: string | undefined): ThroughputCell | null {
        const throughput = this.readings.get(id ?? '');
        if (!throughput) {
            return null;
        }
        let cell = this.cells.get(throughput);
        if (!cell) {
            cell = throughputCell(throughput);
            this.cells.set(throughput, cell);
        }
        return cell;
    }

    /**
     * Read `ids` on `networkId`. A refresh cancels the reads in flight and asks for every id again, keeping
     * the old lines up until the new ones land; otherwise only ids with no reading are asked for.
     */
    load(networkId: string, ids: (string | undefined)[], refresh = false) {
        if (refresh) {
            this.cancel();
        }
        const wanted = [...new Set(ids.map((id) => id ?? ''))].filter(
            (id) => id !== '' && (refresh || (!this.readings.has(id) && !this.pending.has(id)))
        );
        if (!networkId || wanted.length === 0) {
            return;
        }
        wanted.forEach((id) => this.pending.add(id));
        this.subscription.add(
            this.service.findThroughput(networkId, this.field, wanted).subscribe(({ id, throughput }) => {
                this.pending.delete(id);
                // A failed read clears the row rather than leaving the last line up as if it were current.
                if (throughput) {
                    this.readings.set(id, throughput);
                } else {
                    this.readings.delete(id);
                }
            })
        );
    }

    /** Drop every reading, for a change of frontdoor: the ids belong to another network. */
    reset() {
        this.cancel();
        this.readings.clear();
    }

    /** Stop the reads in flight, for a page that is going away. */
    stop() {
        this.cancel();
    }

    private cancel() {
        this.subscription.unsubscribe();
        this.subscription = new Subscription();
        this.pending.clear();
    }
}
